import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Loader2, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { useCreateDepartment } from '@/hooks/useDepartments'
import { createDepartmentSchema, type CreateDepartmentFormData } from '@/schemas/department'
import { showError } from '@/lib/error'

interface CreateDepartmentDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	parentId?: number
	onSuccess?: () => void
}

export function CreateDepartmentDialog({
	open,
	onOpenChange,
	parentId,
	onSuccess,
}: CreateDepartmentDialogProps) {
	const createMutation = useCreateDepartment()

	const form = useForm<CreateDepartmentFormData>({
		resolver: zodResolver(createDepartmentSchema),
		defaultValues: {
			name: '',
			description: '',
			parent_id: parentId,
			status: 1,
		},
	})

	// 当 parentId 变化时更新表单
	useEffect(() => {
		if (open) {
			form.reset({
				name: '',
				description: '',
				parent_id: parentId,
				status: 1,
			})
		}
	}, [open, parentId, form])

	const onSubmit = async (data: CreateDepartmentFormData) => {
		try {
			await createMutation.mutateAsync({
				name: data.name,
				description: data.description || undefined,
				parent_id: data.parent_id,
				status: data.status,
			})
			toast.success('部门创建成功')
			form.reset()
			onOpenChange(false)
			onSuccess?.()
		} catch (error) {
			console.error('创建部门失败:', error)
			showError(error, '创建部门失败')
		}
	}

	const handleClose = () => {
		form.reset()
		onOpenChange(false)
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[450px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Building2 className="h-5 w-5" />
						{parentId ? '新增子部门' : '新增顶级部门'}
					</DialogTitle>
					<DialogDescription>
						填写以下信息创建新的部门
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>部门名称 <span className="text-destructive">*</span></FormLabel>
									<FormControl>
										<Input placeholder="请输入部门名称" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>部门说明</FormLabel>
									<FormControl>
										<textarea
											className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
											placeholder="请输入部门说明（可选）"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<DialogFooter>
							<Button type="button" variant="outline" onClick={handleClose}>
								取消
							</Button>
							<Button type="submit" disabled={createMutation.isPending}>
								{createMutation.isPending && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								创建
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
